import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { api } from "@/lib/api.js";
import { toast } from "sonner";
import { useAuthStore } from "@/lib/auth.js";

interface Flag {
  id: number;
  flagKey: string;
  description: string | null;
  enabledGlobal: boolean;
}

export function SettingsPage() {
  const qc = useQueryClient();
  const canWrite = useAuthStore((s) => s.hasPermission("feature_flag.write"));
  const { data, isLoading } = useQuery({
    queryKey: ["flags"],
    queryFn: () => api.get<{ items: Flag[] }>("/feature-flags"),
  });
  const toggle = useMutation({
    mutationFn: (f: Flag) =>
      api.patch(`/feature-flags/${f.id}`, { enabledGlobal: !f.enabledGlobal }),
    onSuccess: () => {
      toast.success("Flag actualizado");
      qc.invalidateQueries({ queryKey: ["flags"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Configuración"
        description="Feature flags, integraciones y opciones del sistema."
      />
      <section className="card">
        <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <h2 className="font-semibold">Feature flags</h2>
          <p className="text-xs text-slate-500">
            Control global de features. Los overrides por company se gestionan
            desde la ficha de cada Company.
          </p>
        </header>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {isLoading && <li className="px-5 py-6 text-slate-400">Cargando…</li>}
          {data?.items.map((f) => (
            <li key={f.id} className="px-5 py-4 flex items-center justify-between">
              <div>
                <p className="font-medium mono text-sm">{f.flagKey}</p>
                <p className="text-xs text-slate-500">{f.description}</p>
              </div>
              <button
                disabled={!canWrite || toggle.isPending}
                onClick={() => toggle.mutate(f)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${f.enabledGlobal ? "bg-brand-600" : "bg-slate-300 dark:bg-slate-700"}`}
                aria-pressed={f.enabledGlobal}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${f.enabledGlobal ? "translate-x-6" : "translate-x-1"}`}
                />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
